/**
 * Uniform-grid spatial hash for neighbour queries.
 *
 * Circle packing, boids, collision avoidance and Poisson sampling all ask the same
 * question — "what is near this point?" — and all of them turn quadratic without an
 * index. A uniform grid beats a quadtree here because the queries are radius-bounded
 * and the points are roughly evenly spread; there is no tree to walk and no
 * allocation per query.
 *
 * Storage is a bucketed CSR-style layout over typed arrays: no per-cell array objects,
 * so inserting 20 000 circles costs two linear passes and nothing for the GC.
 */
export class SpatialHash {
  readonly cellSize: number;
  readonly cols: number;
  readonly rows: number;

  private readonly x: Float32Array;
  private readonly y: Float32Array;
  /** Start offset of each cell inside `items`. Length cols*rows + 1. */
  private readonly cellStart: Int32Array;
  private readonly items: Int32Array;
  private readonly counts: Int32Array;
  private count = 0;
  private built = false;

  constructor(width: number, height: number, cellSize: number, capacity: number) {
    this.cellSize = Math.max(1, cellSize);
    this.cols = Math.max(1, Math.ceil(width / this.cellSize) + 1);
    this.rows = Math.max(1, Math.ceil(height / this.cellSize) + 1);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.items = new Int32Array(capacity);
    this.cellStart = new Int32Array(this.cols * this.rows + 1);
    this.counts = new Int32Array(this.cols * this.rows);
  }

  private cellIndex(px: number, py: number): number {
    const cx = Math.min(this.cols - 1, Math.max(0, (px / this.cellSize) | 0));
    const cy = Math.min(this.rows - 1, Math.max(0, (py / this.cellSize) | 0));
    return cy * this.cols + cx;
  }

  /** Adds a point. Call `build()` once before querying. */
  add(px: number, py: number): number {
    const id = this.count++;
    this.x[id] = px;
    this.y[id] = py;
    this.built = false;
    return id;
  }

  /** Sorts the points into cells. O(n). */
  build(): void {
    this.counts.fill(0);
    for (let i = 0; i < this.count; i++) this.counts[this.cellIndex(this.x[i], this.y[i])]++;

    let running = 0;
    for (let c = 0; c < this.counts.length; c++) {
      this.cellStart[c] = running;
      running += this.counts[c];
    }
    this.cellStart[this.counts.length] = running;

    const cursor = new Int32Array(this.counts.length);
    for (let i = 0; i < this.count; i++) {
      const c = this.cellIndex(this.x[i], this.y[i]);
      this.items[this.cellStart[c] + cursor[c]++] = i;
    }
    this.built = true;
  }

  /**
   * Calls `visit` for every point within `radius` of (px, py).
   *
   * Returning `false` from the callback stops the search early — the packing loop
   * uses that to bail out as soon as one blocking neighbour is found.
   */
  query(px: number, py: number, radius: number, visit: (id: number) => boolean | void): void {
    if (!this.built) this.build();
    const r = Math.max(0, radius);
    const minCx = Math.max(0, ((px - r) / this.cellSize) | 0);
    const maxCx = Math.min(this.cols - 1, ((px + r) / this.cellSize) | 0);
    const minCy = Math.max(0, ((py - r) / this.cellSize) | 0);
    const maxCy = Math.min(this.rows - 1, ((py + r) / this.cellSize) | 0);

    for (let cy = minCy; cy <= maxCy; cy++) {
      const row = cy * this.cols;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const cell = row + cx;
        const end = this.cellStart[cell + 1];
        for (let k = this.cellStart[cell]; k < end; k++) {
          if (visit(this.items[k]) === false) return;
        }
      }
    }
  }

  get size(): number {
    return this.count;
  }
}

/**
 * Incremental variant for circle packing, where every insert must be visible to the
 * next query. Rebuilding a CSR index per insert would be quadratic, so cells hold
 * growable index lists instead.
 */
export class IncrementalHash {
  readonly cellSize: number;
  private readonly cols: number;
  private readonly rows: number;
  private readonly cells: Int32Array[];
  private readonly cellCounts: Int32Array;

  constructor(width: number, height: number, cellSize: number) {
    this.cellSize = Math.max(1, cellSize);
    this.cols = Math.max(1, Math.ceil(width / this.cellSize) + 1);
    this.rows = Math.max(1, Math.ceil(height / this.cellSize) + 1);
    this.cells = new Array(this.cols * this.rows);
    this.cellCounts = new Int32Array(this.cols * this.rows);
  }

  private index(px: number, py: number): number {
    const cx = Math.min(this.cols - 1, Math.max(0, (px / this.cellSize) | 0));
    const cy = Math.min(this.rows - 1, Math.max(0, (py / this.cellSize) | 0));
    return cy * this.cols + cx;
  }

  insert(px: number, py: number, id: number): void {
    const c = this.index(px, py);
    let list = this.cells[c];
    if (!list) {
      list = new Int32Array(4);
      this.cells[c] = list;
    } else if (this.cellCounts[c] === list.length) {
      const grown = new Int32Array(list.length * 2);
      grown.set(list);
      this.cells[c] = grown;
      list = grown;
    }
    list[this.cellCounts[c]++] = id;
  }

  query(px: number, py: number, radius: number, visit: (id: number) => boolean | void): void {
    const minCx = Math.max(0, ((px - radius) / this.cellSize) | 0);
    const maxCx = Math.min(this.cols - 1, ((px + radius) / this.cellSize) | 0);
    const minCy = Math.max(0, ((py - radius) / this.cellSize) | 0);
    const maxCy = Math.min(this.rows - 1, ((py + radius) / this.cellSize) | 0);

    for (let cy = minCy; cy <= maxCy; cy++) {
      const row = cy * this.cols;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const cell = row + cx;
        const list = this.cells[cell];
        if (!list) continue;
        const n = this.cellCounts[cell];
        for (let k = 0; k < n; k++) {
          if (visit(list[k]) === false) return;
        }
      }
    }
  }
}
